import React, { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { Dropdown } from 'primereact/dropdown';
import { Paginator } from 'primereact/paginator';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import LmsVideoCard from './components/LmsVideoCard';
import LmsVideoFormDialog from './components/LmsVideoFormDialog';
import LmsVideoWatchDialog from './components/LmsVideoWatchDialog';
import LmsCodeDropdown from '../components/LmsCodeDropdown';
import useLmsVideoData from './hooks/useLmsVideoData';
import { useDeleteLmsVideoMutation } from '../../../api';
import toastService from '../../../utils/toastService';
import { useLanguage } from '../../../contexts/LanguageContext';
import { PRIMARY } from '../components/LmsSectionCard';
import { useLmsPermission } from '../hooks/useLmsPermission';
import { LMS } from '../../../constants/permissionCodes';

const ROWS_PER_PAGE_OPTIONS = [12, 24, 48];

/**
 * LMS Eğitim Videoları — YouTube tarzı kart galerisi. Yönetim Sayfaları hub'ından
 
 */
const LmsVideosPage = ({ readOnly = false }) => {
    const { th } = useLanguage();
    const { can } = useLmsPermission();
    const canCreate = !readOnly && can(LMS.TRAINING_CREATE);
    const canUpdate = !readOnly && can(LMS.TRAINING_UPDATE);
    const canDelete = !readOnly && can(LMS.TRAINING_DELETE);

    const visibilityOptions = [
        { label: th('lmsVideo.visibilityPublic'), value: true },
        { label: th('lmsVideo.visibilityPrivate'), value: false }
    ];

    const { lmsVideoData, isLoading, isFetching, refetch, lazyParams, onPage, activeFilters, handleFilterChange, initFilters } = useLmsVideoData();

    const [deleteLmsVideo] = useDeleteLmsVideoMutation();

    const [formDialogVisible, setFormDialogVisible] = useState(false);
    const [editingVideo, setEditingVideo] = useState(null);
    const [watchDialogVisible, setWatchDialogVisible] = useState(false);
    const [watchingVideoId, setWatchingVideoId] = useState(null);

    const handleAddNew = () => {
        setEditingVideo(null);
        setFormDialogVisible(true);
    };

    const handleEdit = (video) => {
        setEditingVideo(video);
        setFormDialogVisible(true);
    };

    const handleWatch = (video) => {
        setWatchingVideoId(video.id);
        setWatchDialogVisible(true);
    };

    const handleFormSuccess = () => {
        refetch();
    };

    const handleRowsChange = (rows) => {
        onPage({ page: 0, first: 0, rows });
    };

    const handleDelete = (video) => {
        confirmDialog({
            message: th('lmsVideo.confirmDeleteMessage'),
            header: th('common.delete'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const result = await deleteLmsVideo({ id: video.id, hardDelete: false }).unwrap();
                    if (result?.isSuccessful === false) {
                        throw new Error(result?.errorMessage || th('lmsVideo.deleteError'));
                    }
                    toastService.success(th('lmsVideo.deleteSuccess'), th('common.success'));
                    refetch();
                } catch (error) {
                    toastService.error(error?.message || th('lmsVideo.deleteError'), th('common.error'));
                }
            }
        });
    };

    const items = lmsVideoData?.items || [];
    const totalCount = lmsVideoData?.totalCount || 0;
    const rangeStart = totalCount === 0 ? 0 : lazyParams.first + 1;
    const rangeEnd = Math.min(lazyParams.first + lazyParams.rows, totalCount);

    return (
        <div>
            <div className="flex align-items-start justify-content-between mb-4 flex-wrap gap-3">
                <div>
                    <h1 style={{ color: PRIMARY, margin: 0, fontSize: '1.75rem' }}>{th('lmsVideo.title')}</h1>
                    <span className="text-color-secondary block mt-1">{th('lmsVideo.subtitle')}</span>
                </div>
                {canCreate && (
                    <Button
                        label={th('lmsVideo.addButton')}
                        icon="pi pi-plus"
                        onClick={handleAddNew}
                        className="border-round-3xl"
                        style={{ backgroundColor: PRIMARY, borderColor: PRIMARY, padding: '0.75rem 1.5rem' }}
                    />
                )}
            </div>

            <div
                className="flex align-items-center justify-content-between mb-4 flex-wrap gap-3"
                style={{ backgroundColor: '#F9FAFB', border: '1px solid #EAECF0', borderRadius: '1.25rem', padding: '1rem 1.25rem' }}
            >
                <div className="flex align-items-center gap-3 flex-wrap">
                    <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        <InputText
                            value={activeFilters.tittle}
                            onChange={(e) => handleFilterChange({ ...activeFilters, tittle: e.target.value })}
                            placeholder={th('lmsVideo.filterSearchPlaceholder')}
                            className="border-round-3xl"
                            style={{ width: '15rem' }}
                        />
                    </span>

                    <InputText
                        value={activeFilters.modelCode}
                        onChange={(e) => handleFilterChange({ ...activeFilters, modelCode: e.target.value })}
                        placeholder={th('lmsVideo.filterModelCodePlaceholder')}
                        className="border-round-3xl"
                        style={{ width: '11rem' }}
                    />

                    <LmsCodeDropdown
                        groupCode="VIDEO_FEE_TYPE"
                        value={activeFilters.feeType}
                        onChange={(value) => handleFilterChange({ ...activeFilters, feeType: value })}
                        placeholder={th('lmsVideo.filterFeeTypeAll')}
                        className="border-round-3xl w-12rem"
                    />

                    <Dropdown
                        value={activeFilters.isPublic}
                        options={visibilityOptions}
                        onChange={(e) => handleFilterChange({ ...activeFilters, isPublic: e.value ?? null })}
                        placeholder={th('lmsVideo.filterVisibilityAll')}
                        showClear
                        className="border-round-3xl w-12rem"
                    />

                    <div className="flex align-items-center gap-2">
                        <InputSwitch checked={!!activeFilters.onlyWithVideo} onChange={(e) => handleFilterChange({ ...activeFilters, onlyWithVideo: e.value })} />
                        <span className="text-sm">{th('lmsVideo.filterOnlyWithVideo')}</span>
                    </div>
                </div>

                <Button label={th('lmsVideo.clearFiltersLabel')} text onClick={initFilters} />
            </div>

            {(isLoading || isFetching) && (
                <div className="flex align-items-center justify-content-center" style={{ minHeight: '12rem' }}>
                    <ProgressSpinner style={{ width: '3rem', height: '3rem' }} strokeWidth="4" />
                </div>
            )}

            {!isLoading && !isFetching && items.length === 0 && <div className="text-color-secondary p-4 text-center">{th('lmsVideo.emptyMessage')}</div>}

            {!isLoading && !isFetching && items.length > 0 && (
                <>
                    <div className="grid">
                        {items.map((video) => (
                            <LmsVideoCard key={video.id} video={video} onWatch={handleWatch} onEdit={canUpdate ? handleEdit : undefined} onDelete={canDelete ? handleDelete : undefined} readOnly={readOnly} />
                        ))}
                    </div>

                    <div className="flex align-items-center justify-content-between mt-4 flex-wrap gap-3">
                        <span className="text-color-secondary text-sm">{th('lmsVideo.paginationReport', { first: rangeStart, last: rangeEnd, totalRecords: totalCount })}</span>

                        <Paginator first={lazyParams.first} rows={lazyParams.rows} totalRecords={totalCount} template="PrevPageLink PageLinks NextPageLink" onPageChange={onPage} className="border-none" style={{ background: 'transparent' }} />

                        <div className="flex align-items-center gap-2">
                            <span className="text-color-secondary text-sm">{th('lmsVideo.rowsPerPageLabel')}</span>
                            <Dropdown value={lazyParams.rows} options={ROWS_PER_PAGE_OPTIONS} onChange={(e) => handleRowsChange(e.value)} className="w-6rem" />
                        </div>
                    </div>
                </>
            )}

            {!readOnly && <LmsVideoFormDialog visible={formDialogVisible} onHide={() => setFormDialogVisible(false)} editingVideo={editingVideo} onSuccess={handleFormSuccess} />}

            <LmsVideoWatchDialog visible={watchDialogVisible} onHide={() => setWatchDialogVisible(false)} videoId={watchingVideoId} />

            {!readOnly && <ConfirmDialog />}
        </div>
    );
};

export default LmsVideosPage;
